import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, Lock, MessageCircle } from "lucide-react";
import QuestionDialog from "@/components/QuestionDialog";
import MaterialDiscussion from "@/components/MaterialDiscussion";
import useMaterialAnalytics from "@/lib/useMaterialAnalytics";

const TYPE_LABEL = {
  ARTICLE: "Статья",
  RECIPE: "Рецепт",
  FORMULATION: "Рецептура",
  GUIDE: "Гайд",
  REFERENCE: "Справочник",
};

export default function ProtectedMaterialDetail() {
  const { pslug, mslug } = useParams();
  const nav = useNavigate();
  const [material, setMaterial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [askOpen, setAskOpen] = useState(false);
  const contentRef = useRef(null);
  useMaterialAnalytics(material?.id, contentRef);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `/products/${encodeURIComponent(pslug)}/materials/${encodeURIComponent(mslug)}`
      );
      setMaterial(data);
      setDenied(false); setNotFound(false);
    } catch (e) {
      if (e.response?.status === 403) setDenied(true);
      else if (e.response?.status === 404) setNotFound(true);
      else toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, [pslug, mslug]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="min-h-screen bg-cream" data-testid="material-detail-loading">
        <div className="max-w-3xl mx-auto px-5 py-10">
          <div className="h-6 w-24 bg-sand/40 rounded animate-pulse" />
          <div className="h-10 w-3/4 bg-sand/40 rounded animate-pulse mt-4" />
          <div className="h-64 bg-sand/30 rounded-3xl animate-pulse mt-6" />
        </div>
      </div>
    );
  }
  if (denied) return <Blocked title="Нет доступа" desc="Материал доступен только владельцам этого продукта." pslug={pslug} />;
  if (notFound || !material) return <Blocked title="Материал не найден" desc="Возможно, он ещё не опубликован." pslug={pslug} />;

  return (
    <div className="min-h-screen bg-cream" data-testid="material-detail-page">
      <header className="max-w-3xl mx-auto px-5 pt-5 flex items-center gap-3">
        <button
          onClick={() => nav(`/products/${encodeURIComponent(pslug)}`)}
          data-testid="back-to-product-button"
          className="p-2 -ml-2 rounded-full hover:bg-sand/50"
          aria-label="К продукту"
        >
          <ChevronLeft className="w-5 h-5 text-espresso" />
        </button>
        <div className="text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
          {TYPE_LABEL[material.material_type] || material.material_type}
        </div>
      </header>

      <article ref={contentRef} className="max-w-3xl mx-auto px-5 pt-4 pb-16">
        <h1 className="font-serif text-4xl leading-tight text-espresso" data-testid="material-title">
          {material.title}
        </h1>
        {material.short_description && (
          <p className="text-lg text-muted-foreground mt-3" data-testid="material-short">
            {material.short_description}
          </p>
        )}

        {material.cover_image && (
          <figure className="mt-6" data-testid="material-cover-figure">
            <img
              src={material.cover_image}
              alt={material.title}
              className="block mx-auto max-w-full h-auto max-h-[70vh] rounded-3xl"
              onError={(e) => { e.target.parentElement.style.display = "none"; }}
            />
          </figure>
        )}

        <div
          className="anokina-content font-serif text-espresso mt-8 leading-relaxed text-[17px]"
          data-testid="material-body"
          dangerouslySetInnerHTML={{ __html: renderBody(material.body) }}
        />

        <div className="mt-8 flex items-center gap-3 flex-wrap">
          <button
            onClick={() => setAskOpen(true)}
            data-testid="material-ask-button"
            className="chip px-5 py-2 border transition-colors flex items-center gap-2 bg-white text-espresso border-[#E1D2B8] hover:bg-sand/40"
          >
            <MessageCircle className="w-4 h-4" />
            Задать вопрос
          </button>
        </div>
        <MaterialDiscussion material={material} />
      </article>
      <QuestionDialog open={askOpen} onOpenChange={setAskOpen} material={material} />
    </div>
  );
}

function Blocked({ title, desc, pslug }) {
  return (
    <div className="min-h-screen bg-cream flex flex-col items-center justify-center px-5" data-testid="material-detail-blocked">
      <Lock className="w-10 h-10 text-espresso/40" />
      <p className="font-serif text-2xl text-espresso mt-4">{title}</p>
      <p className="text-sm text-muted-foreground mt-2 text-center max-w-md">{desc}</p>
      <Link
        to={pslug ? `/products/${encodeURIComponent(pslug)}` : "/account"}
        className="chip bg-espresso text-white mt-6 px-5 py-2"
      >
        Назад
      </Link>
    </div>
  );
}

function renderBody(body) {
  if (!body) return "<p>Содержимое будет добавлено скоро.</p>";
  if (/^\s*<(?:p|h[1-6]|ul|ol|blockquote|hr|img|figure|div|span|strong|em)/i.test(body)) return body;
  return body
    .split(/\n{2,}/)
    .map((p) => `<p>${escapeHtml(p).replace(/\n/g, "<br/>")}</p>`)
    .join("");
}
function escapeHtml(s) {
  return String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}
